import { WidgetTheme } from '@wix/wix-help-widget-common/types';

import { renderModel } from './renderModel';
import { getWidgetTheme, getWidgetVariant, WidgetVariant } from './theme';

/**
 * Style traits per resolved theme — the single statement of how each theme LOOKS
 * for decisions CSS cannot make (WDS component props, icon-set component swaps).
 * Class-level styling is already handled by the .widget-theme-* gates in SCSS
 * (see mixin.scss); logic (data fetching, filters, URLs, BI) stays on
 * renderModel/origin.
 *
 * Blocks note: originToTheme folds Blocks into the studio theme, so
 * `studioLayout` is true for Blocks too — matching how the SCSS gates behave.
 *
 * Most traits are a pure function of the theme (the TRAITS table below); the one
 * exception is `variant`, a within-theme deviation for a host family — see
 * getWidgetVariant. Reach for a variant only when a couple of props differ;
 * anything wider is a theme.
 */
export type ThemeTraits = {
  theme: WidgetTheme;
  /**
   * Within-theme deviation for a host family, `null` for a plain theme. Carries
   * no palette: only the components a variant actually changes read it.
   */
  variant: WidgetVariant | null;
  /** Compact editor-panel chrome: studio 1, Blocks, and Studio 2.0. */
  studioLayout: boolean;
  /** Editor 3 look (odeditor): own icon set, medium densities, flat footer. */
  editor3Layout: boolean;
  /** Dark palette (odeditor-dark / Studio 2.0). */
  dark: boolean;
  /** Which icon components to render. */
  iconSet: 'default' | 'editor3';
};

const TRAITS: Record<WidgetTheme, Omit<ThemeTraits, 'theme' | 'variant'>> = {
  default: { studioLayout: false, editor3Layout: false, dark: false, iconSet: 'default' },
  studio: { studioLayout: true, editor3Layout: false, dark: false, iconSet: 'default' },
  odeditor: { studioLayout: false, editor3Layout: true, dark: false, iconSet: 'editor3' },
  'odeditor-dark': { studioLayout: true, editor3Layout: false, dark: true, iconSet: 'default' },
  wixel: { studioLayout: false, editor3Layout: false, dark: false, iconSet: 'default' },
};

/**
 * Resolves at the point of call. Prefer calling inside render (or via
 * useThemeTraits) — a module-level `const` freezes the first value, which is
 * correct in the app (the theme is fixed for a page's lifetime) but stale when
 * Storybook or tests swap args within one module instance.
 *
 * Plain function, safe outside React (thunks, module helpers).
 */
export const getThemeTraits = (): ThemeTraits => {
  const { origin, theme } = renderModel.getQueryParams() ?? {};
  const originApp = typeof origin === 'string' ? origin : null;
  const resolved = getWidgetTheme(originApp, theme);
  return { theme: resolved, variant: getWidgetVariant(originApp, resolved), ...TRAITS[resolved] };
};

/** Hook-shaped alias so component call sites read like other hooks. */
export const useThemeTraits = (): ThemeTraits => getThemeTraits();
